import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  FileParameter,
  Permission,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import { SUPPORT_CASES_QUERY_KEY } from './useReachProSupportCases';

type CreateReachProSupportCaseVars = {
  topic: string;
  eventId: number;
  listingId?: number | null;
  saleId?: number | null;
  attachments: File[];
};

export function useCreateReachProSupportCase() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportCreatePermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_Create
  );

  return useMutation({
    mutationFn: async (vars: CreateReachProSupportCaseVars) => {
      if (!hasReachProSupportFeature) {
        throw new Error('Support Center feature is not enabled');
      }
      if (!hasSupportCreatePermission) {
        throw new Error('Missing permission to create support cases');
      }

      return tryInvokeApi(
        async () => {
          const fileParams: FileParameter[] = vars.attachments.map((f) => ({
            data: f,
            fileName: f.name,
          }));
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).createSupportCase(
            vars.topic,
            vars.eventId,
            vars.listingId,
            vars.saleId,
            fileParams
          );
        },
        (error) => {
          showErrorDialog('ReachProSupportCaseClient.createSupportCase', error);
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUPPORT_CASES_QUERY_KEY] });
    },
  });
}
